import {
  Alert,
  Item,
  ItemContent,
  ItemHeader,
  ItemTitle,
  ScrollArea,
  Sentinel,
  Skeleton,
  Spinner,
  useInfiniteScroll,
} from '@ibsys/kit';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import type { KnowledgeSetListItem } from '@/entities/knowledge-set';
import { useAppNavigate } from '@/shared/lib';
import { cn } from '@/shared/lib';

type Props = {
  items: KnowledgeSetListItem[];
  isLoading: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
};

export const KnowledgeSetList: FC<Props> = ({ items, isLoading, hasNextPage, onLoadMore }) => {
  const { t } = useTranslation('knowledgeSets');
  const { id } = useParams<{ id?: string }>();
  const navigate = useAppNavigate();
  const viewportRef = useRef<HTMLDivElement>(null);
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore,
    hasMore: hasNextPage,
    isLoading: false,
    threshold: 500,
    root: viewportRef,
  });

  const handleSelect = (knowledgeSetId: number) => {
    // Сохраняем текущие query-параметры (в т.ч. показ удалённых) при открытии карточки сета
    navigate('/knowledge-sets/:id', { id: knowledgeSetId.toString() }, { saveParams: true });
  };

  if (isLoading && items.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-14" />
        ))}
      </div>
    );
  }

  if (!items.length) {
    return <Alert className="text-muted-foreground p-4" title={t('listIsEmpty')} />;
  }

  return (
    <ScrollArea viewPortProps={{ ref: viewportRef }}>
      <ul className="flex flex-col gap-2">
        {items.map((knowledgeSet) => {
          const isActive = id === knowledgeSet.id.toString();
          return (
            <li key={knowledgeSet.id} role="button" tabIndex={-1} onClick={() => handleSelect(knowledgeSet.id)}>
              <Item
                variant={isActive ? 'muted' : 'outline'}
                className={cn(
                  'hover:bg-muted cursor-pointer overflow-x-hidden',
                  knowledgeSet.isDeleted && 'opacity-50',
                )}
              >
                <ItemHeader>
                  <ItemTitle className="text-sm font-medium">{knowledgeSet.name}</ItemTitle>
                </ItemHeader>
                <ItemContent className="text-xs text-muted-foreground">
                  {t('nodeCount', { count: knowledgeSet.nodeCount })}
                </ItemContent>
              </Item>
            </li>
          );
        })}
        <Sentinel ref={sentinelRef}>
          {hasNextPage && (
            <div className="w-full flex justify-center py-6">
              <Spinner className="size-8" />
            </div>
          )}
        </Sentinel>
      </ul>
    </ScrollArea>
  );
};
